import { useState, useEffect, useCallback } from 'react'
import { getCitatorRelatedCases } from '../fetch/get-citator-related-cases'
import { CitatorCasesResponse } from '../schema/citation-metadata-schema'

interface UseGetCitatorRelatedCasesState {
  data: CitatorCasesResponse | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

/**
 * Use Get Citator Related Cases
 * Fetches related cases for a given parent_id.
 *
 * @param parent_id - The parent ID of the case
 * @returns data, isLoading, isError, and refetch function
 */
export function useGetCitatorRelatedCases(parent_id: string): UseGetCitatorRelatedCasesState {
  const [data, setData] = useState<CitatorCasesResponse | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const fetchData = useCallback(async () => {
    if (!parent_id) {
      setData(undefined)
      setIsError(true)
      return
    }

    setIsLoading(true)
    setIsError(false)

    try {
      const result = await getCitatorRelatedCases(parent_id)

      if (!result) {
        setIsError(true)
        setData(undefined)
      } else {
        setData(result)
        setIsError(false)
      }
    } catch (err) {
      setIsError(true)
      setData(undefined)
      console.error('Error fetching related cases:', err)
    } finally {
      setIsLoading(false)
    }
  }, [parent_id])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const refetch = useCallback(() => {
    fetchData()
  }, [fetchData])

  return {
    data,
    isLoading,
    isError,
    refetch,
  }
}
